/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDisaEntryForm.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing DISA Entry form state and submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createDisaEntry, updateDisaEntry } from '../api/disaEntriesApi';
import { FormState, ValidationErrors } from '../types';
import { validateDisaEntryForm } from '../utils/validators';
import { buildPayloadSettings, generateContextFromName } from '../utils/settingsHelpers';
import { getDefaultFormState } from '../utils/formDefaults';

/**
 * Hook for DISA Entry form management
 */
export function useDisaEntryForm(
  initialData: FormState | null,
  id: string | undefined,
  isEditMode: boolean
) {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const lastSaveRef = useRef<number>(0);

  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formData, setFormData] = useState<FormState>(
    initialData || defaultFormState
  );
  const [originalData, setOriginalData] = useState<FormState>(
    initialData || defaultFormState
  );
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const lastInitialDataRef = useRef<string>('');
  const initializedRef = useRef(false);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  // Sync with initialData when it changes (only if it's actually different)
  useEffect(() => {
    // If no initial data, use default state (only on first mount or when explicitly needed)
    if (!initialData) {
      if (!initializedRef.current && !isEditMode) {
        initializedRef.current = true;
        setFormData(defaultFormState);
        setOriginalData(defaultFormState);
        setErrors({});
      }
      return;
    }

    // Compare serialized versions to avoid unnecessary updates
    const currentSerialized = JSON.stringify(initialData);
    if (lastInitialDataRef.current === currentSerialized) {
      return;
    }

    lastInitialDataRef.current = currentSerialized;
    initializedRef.current = true;
    setFormData(initialData);
    setOriginalData(initialData);
    setErrors({});
  }, [initialData, isEditMode, defaultFormState]);

  const hasChanges = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(originalData),
    [formData, originalData]
  );

  const handleChange = useCallback(
    (field: keyof FormState, value: string | number | boolean | null) => {
      setFormData((prev) => {
        const next = { ...prev, [field]: value } as FormState;
        if (field === 'name' && typeof value === 'string' && value.trim()) {
          next.context = generateContextFromName(value);
        }
        return next;
      });

      if (errors[field as string]) {
        setErrors((prev) => {
          const updated = { ...prev };
          delete updated[field as string];
          return updated;
        });
      }
    },
    [errors]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSettingsChange = useCallback((key: keyof FormState['settings'], value: any) => {
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [key]: value
      }
    }));
  }, []);

  const validateForm = useCallback(() => {
    const validationErrors = validateDisaEntryForm(formData, t);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      showErrorRef.current(t('disaEntries:messages.fixValidationErrors'));
      return false;
    }

    return true;
  }, [formData, t]);

  // Handle submit - Always-Active Save Standard: always attempt to save
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return undefined;
    }

    const now = Date.now();
    if (now - lastSaveRef.current < 1000) {
      return undefined;
    }
    lastSaveRef.current = now;

    setIsSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        context: (formData.context || generateContextFromName(formData.name)).trim(),
        password: formData.password.trim(),
        classOfServicesId: formData.classOfServicesId,
        enabled: formData.enabled,
        settings: buildPayloadSettings(formData.settings)
      };

      const result = isEditMode
        ? await updateDisaEntry(id!, payload)
        : await createDisaEntry(payload);

      showSuccess(
        isEditMode
          ? t('disaEntries:messages.saveSuccessUpdate')
          : t('disaEntries:messages.saveSuccessCreate')
      );

      const persistedContext = payload.context;
      const nextOriginal = {
        ...formData,
        context: persistedContext
      };
      setOriginalData(nextOriginal);
      setFormData(nextOriginal);

      // Return saved data (navigation is now handled by useSaveWithAction)
      return result || { ...payload, id: result?.id };
    } catch (error) {
      showErrorRef.current(error?.message || t('disaEntries:messages.saveError'));
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [formData, hasChanges, id, isEditMode, showInfo, showSuccess, t, validateForm]);

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormData(originalData);
      setErrors({});
    } else {
      const defaultState = getDefaultFormState();
      setFormData(defaultState);
      setOriginalData(defaultState);
      setErrors({});
    }
  }, [isEditMode, originalData]);

  const resetForm = useCallback((newData: FormState) => {
    setFormData(newData);
    setOriginalData(newData);
    setErrors({});
  }, []);

  return {
    formData,
    setFormData,
    originalData,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    handleSettingsChange,
    handleSubmit,
    handleCancel,
    resetForm,
    validateForm
  };
}

